/* Homepage and opening tutorial panels use desktop stepping. Touch input stays native; written instructions scroll freely. */
(() => {
  const body = document.body;
  if (!body || !body.classList.contains('fw-scroll-site')) return;
  const isCompare = Boolean(document.querySelector('.fc-page'));
  const isTutorial = Boolean(document.querySelector('.fo-tutorial'));
  const isContact = Boolean(document.querySelector('.fw-contact'));
  if (!body.classList.contains('fw-scroll-home') && !isTutorial && !isContact) return;
  // Theme editor: sections reload and get selected constantly; keep a normal scrolling page there.
  if (window.Shopify && window.Shopify.designMode) return;

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wide = window.matchMedia('(min-width: 1081px)');
  const nativeInput = window.matchMedia('(pointer: coarse), (max-width: 1100px)');
  let nativeGestures = nativeInput.matches || navigator.maxTouchPoints > 0;
  const isHome = body.classList.contains('fw-scroll-home');
  const isTrioProduct = body.classList.contains('fw-trio-product-page');
  const MIN_HEIGHT = 440;
  const WHEEL_THRESHOLD = 24;
  const MOMENTUM_GAP = 180;
  const EDGE = 2;

  // Slides inside long sections (each starts a panel). Article/policy H2s split long reading pages.
  const SLIDES = [
    '[data-fw-slide]',
    '.fo-tutorial-video',
    '.fo-tutorial-guidance',
    '.fo-tutorial-guide',
    '.fo-tutorial-support',
    '.fo-compare-section',
    '.fo-quality-rotator',
    '.fo-compare-story',
    '.fo-article__content > h2',
    '.fo-related',
    '.fo-faq__support',
    '.fo-policy-page__inner > h2',
    '.shopify-policy__body h2',
  ].join(', ');

  // Interactive areas where vertical gestures belong to the control, not to panel navigation.
  const FIELD = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
  const IGNORE = `${FIELD}, dialog, video:not(.fw-framed-hero__film), iframe, [data-fw-scroll-ignore], quantity-selector-component, [role="slider"]`;

  const header = document.querySelector('[data-fw-header]');
  const controls = isCompare ? document.querySelector('.fc-controls') : ((isHome || isTrioProduct) ? document.querySelector('.fw-panel-controls') : null);
  const previousControl = controls?.querySelector('[data-fw-panel-previous]');
  const nextControl = controls?.querySelector('[data-fw-panel-next]');

  let active = false;
  let animating = false;
  let frame = 0;
  let fallbackTimer = 0;
  let settleTimer = 0;
  let wheelTotal = 0;
  let lastWheelAt = 0;
  let lastWheelSize = 0;
  let gestureLocked = false;
  let lockedAt = 0;
  let currentIndex = -1;

  /* ---------- Panels ---------- */
  const viewport = () => window.innerHeight;
  const maxScroll = () => Math.max(0, root.scrollHeight - viewport());
  // Space reserved for the sticky header (0 on the home page while panels sit under the overlay header).
  // Support uses an overlay header and already reserves space inside its form panel.
  const offset = () => 0;
  const readingNatively = () => {
    if (!isTutorial) return false;
    const guide = document.querySelector('.fw-guide-card');
    return Boolean(guide && window.scrollY >= guide.getBoundingClientRect().top + window.scrollY - EDGE);
  };
  const visible = (el) => el.offsetHeight > 0 && getComputedStyle(el).display !== 'none';

  /** Ordered panels: { el, top, bottom } in document coordinates (top is the resting scroll position). */
  function panels() {
    // A section marked [data-fw-join] (the home press strip) belongs to the panel before it.
    const sections = [...document.querySelectorAll('#MainContent > .shopify-section')].filter(
      (section) => visible(section) && !section.querySelector(':scope > [data-fw-join]')
    );
    const candidates = [];
    sections.forEach((section) => {
      candidates.push(section);
      section.querySelectorAll(SLIDES).forEach((slide) => {
        if (visible(slide)) candidates.push(slide);
      });
    });
    const policy = document.querySelector('#MainContent > .shopify-policy__container');
    if (policy) {
      candidates.push(policy);
      policy.querySelectorAll(SLIDES).forEach((slide) => candidates.push(slide));
    }
    const footer = document.querySelector('.fw-footer');
    if (footer && visible(footer)) candidates.push(footer);

    const pad = offset();
    const vh = viewport();
    const minGap = Math.max(160, vh * 0.3);
    const limit = maxScroll();
    const list = [];
    candidates
      .map((el) => ({ el, top: Math.min(limit, Math.max(0, el.getBoundingClientRect().top + window.scrollY - pad)) }))
      .sort((a, b) => a.top - b.top)
      .forEach((item) => {
        const previous = list[list.length - 1];
        // Merge stops that sit too close together into one readable panel.
        if (previous && item.top - previous.top < minGap) return;
        list.push(item);
      });
    list.forEach((item, i) => {
      item.bottom = i < list.length - 1 ? list[i + 1].top : limit + vh;
    });
    return list;
  }

  function indexAt(list, y = window.scrollY) {
    let index = 0;
    list.forEach((item, i) => {
      if (item.top <= y + EDGE) index = i;
    });
    return index;
  }

  const height = (item) => item.bottom - item.top;
  const fits = (item) => height(item) <= viewport() + EDGE;

  /** Where a panel rests: its top, or (coming from below) the end of a tall panel. */
  function restFor(item, direction) {
    return direction < 0 && !fits(item) ? item.bottom - viewport() : item.top;
  }

  /** True when the current panel still has content to reveal in that direction. */
  function canScrollWithin(direction) {
    const list = panels();
    const item = list[indexAt(list)];
    if (!item) return false;
    const y = window.scrollY;
    if (direction > 0) return item.bottom > y + viewport() + EDGE && y < maxScroll() - EDGE;
    return y > item.top + EDGE;
  }

  /* ---------- State ---------- */
  function zoomed() {
    return Boolean(window.visualViewport && window.visualViewport.scale > 1.01);
  }

  function computeActive() {
    nativeGestures = nativeInput.matches || navigator.maxTouchPoints > 0;
    root.classList.toggle('fw-native-gestures', nativeGestures);
    const next = viewport() >= MIN_HEIGHT && !zoomed();
    if (next !== active) {
      active = next;
      root.classList.toggle('fw-studio-active', active);
      if (!active) {
        cancelAnimation();
        header?.classList.remove('fw-header--on-light');
      }
    }
    if (controls) controls.hidden = !active || nativeGestures;
    markCurrent();
  }

  function updateTutorialHeader() {
    if ((!isTutorial && !isCompare) || !header) return;
    const probe = header.getBoundingClientRect().bottom + 1;
    const light = [...document.querySelectorAll(isCompare ? '.fc-comparison' : '.fo-tutorial-video--light')].some((el) => {
      const rect = el.getBoundingClientRect();
      return rect.top <= probe && rect.bottom > probe;
    });
    header.classList.toggle('fw-header--tutorial-light', light);
    const guide = isTutorial && [...document.querySelectorAll('.fo-tutorial-guidance')].some((el) => {
      const rect = el.getBoundingClientRect();
      return rect.top <= probe && rect.bottom > probe;
    });
    header.classList.toggle('fw-header--tutorial-guide', Boolean(guide));
  }

  function markCurrent() {
    updateTutorialHeader();
    const footer = document.querySelector('.fw-footer--gallery');
    const footerRect = footer?.getBoundingClientRect();
    header?.classList.toggle('fw-header--on-footer', Boolean(footerRect && footerRect.top <= (header?.offsetHeight || 72) / 2 && footerRect.bottom > 0));
    const list = panels();
    const index = indexAt(list);
    currentIndex = index;
    if (previousControl) previousControl.disabled = index === 0 && window.scrollY <= EDGE;
    if (nextControl) nextControl.disabled = window.scrollY >= maxScroll() - EDGE;
    list.forEach((item, i) => {
      item.el.classList.toggle('is-current', i === index);
      item.el.querySelector?.('.fw-scene, .fw-hero, .fw-press, .fw-builder')?.classList.toggle('is-current', i === index);
    });
    // Light-wall flag: the header stays transparent over that panel's own dark-brown fade (CSS).
    const light = isHome && active && Boolean(list[index]?.el.querySelector?.('.fw-scene--light-wall'));
    header?.classList.toggle('fw-header--on-light', light);
  }

  /* ---------- Movement ---------- */
  function cancelAnimation() {
    if (frame) cancelAnimationFrame(frame);
    window.clearTimeout(fallbackTimer);
    frame = 0;
    animating = false;
  }

  function scrollInstant(y) {
    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
  }

  function animateTo(y, done) {
    cancelAnimation();
    const target = Math.min(Math.max(0, Math.round(y)), maxScroll());
    const start = window.scrollY;
    const distance = target - start;
    if (Math.abs(distance) < 1) {
      markCurrent();
      done?.();
      return;
    }
    if (reducedMotion.matches) {
      scrollInstant(target);
      markCurrent();
      done?.();
      return;
    }
    const duration = Math.min(950, 520 + Math.abs(distance) * 0.25);
    const began = performance.now();
    animating = true;
    const finish = () => {
      cancelAnimation();
      scrollInstant(target);
      markCurrent();
      done?.();
    };
    const tick = (now) => {
      const progress = Math.min(1, (now - began) / duration);
      const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
      scrollInstant(start + distance * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
      else finish();
    };
    frame = requestAnimationFrame(tick);
    // If frames are throttled (background tab), still land on the panel and release input.
    fallbackTimer = window.setTimeout(finish, duration + 400);
  }

  function goTo(index, direction = 0, done) {
    const list = panels();
    if (!list.length) return;
    const clamped = Math.min(Math.max(index, 0), list.length - 1);
    animateTo(restFor(list[clamped], direction), done);
  }

  /** One deliberate step. Returns false when nothing moved (already at the very start or end). */
  function step(direction) {
    const list = panels();
    const index = indexAt(list);
    const target = index + direction;
    if (target < 0 || target >= list.length) {
      const rest = direction < 0 ? 0 : maxScroll();
      if (Math.abs(rest - window.scrollY) <= EDGE) return false;
      animateTo(rest);
      return true;
    }
    goTo(target, direction);
    return true;
  }

  /** After native scrolling, rest on a panel edge (never part-way between two panels). */
  function settle() {
    if (!active || nativeGestures || readingNatively() || animating || document.activeElement?.matches(FIELD)) return;
    const list = panels();
    if (!list.length) return;
    const vh = viewport();
    const y = window.scrollY;
    const index = indexAt(list, y);
    const item = list[index];
    const next = list[index + 1];
    if (isTutorial && item.el.matches('.fo-tutorial-guidance') && item.el.querySelector('details[open]') && y + vh <= item.bottom + EDGE) {
      markCurrent();
      return;
    }
    if (Math.abs(y - item.top) <= EDGE || y >= maxScroll() - EDGE) {
      markCurrent();
      return;
    }
    let target;
    if (!fits(item)) {
      // Inside a tall panel: leave reading position alone unless the next panel is half in view.
      if (y + vh <= item.bottom + EDGE || !next) {
        markCurrent();
        return;
      }
      target = y + vh - item.bottom > vh / 2 ? next.top : item.bottom - vh;
    } else {
      target = next && y - item.top > (next.top - item.top) / 2 ? next.top : item.top;
    }
    if (Math.abs(target - y) > EDGE) animateTo(target);
    else markCurrent();
  }

  /* ---------- Where not to interfere ---------- */
  function overlayOpen() {
    return Boolean(
      body.classList.contains('fw-menu-open') ||
        [...document.querySelectorAll('dialog[open], [aria-modal="true"]:not([hidden]):not(dialog)')].some(visible)
    );
  }

  function scrollsOnItsOwn(target, direction) {
    let el = target instanceof Element ? target : null;
    while (el && el !== body && el !== root) {
      const style = getComputedStyle(el);
      if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight + 1) {
        if (direction > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
        if (direction < 0 && el.scrollTop > 0) return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  function ignoreTarget(target) {
    if (overlayOpen()) return true;
    if (!(target instanceof Element)) return false;
    // A hovered, unfocused Support field is part of the page gesture. Otherwise native
    // scrolling moves a little, then settle() snaps it back. Editing stays native,
    // and scrollsOnItsOwn() still protects an overflowing message box.
    const field = target.closest(FIELD);
    if (isContact && field?.closest('.fw-contact') && !document.activeElement?.matches(FIELD)) return false;
    return Boolean(target.closest(IGNORE));
  }

  /* ---------- Wheel / trackpad ---------- */
  function onWheel(event) {
    if (!active || nativeGestures || readingNatively() || event.defaultPrevented || event.ctrlKey || event.metaKey) return;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    if (ignoreTarget(event.target)) return;
    const direction = Math.sign(event.deltaY);
    if (!direction) return;
    if (scrollsOnItsOwn(event.target, direction)) return;

    const now = performance.now();
    const gap = now - lastWheelAt;
    const size = Math.abs(event.deltaY) * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport() : 1);
    lastWheelAt = now;

    if (gestureLocked && !animating) {
      // Momentum tail ends when the stream pauses or a clearly new push starts.
      const newPush = now - lockedAt > 450 && size > 30 && size > lastWheelSize * 1.6;
      if (gap >= MOMENTUM_GAP || newPush) {
        gestureLocked = false;
        wheelTotal = 0;
      }
    }

    if (!animating && !gestureLocked && canScrollWithin(direction)) {
      // Tall panel: let the browser scroll it natively to its end first.
      lastWheelSize = size;
      return;
    }

    event.preventDefault();

    if (animating || gestureLocked) {
      lastWheelSize = size;
      return;
    }

    lastWheelSize = size;
    if (gap > 260) wheelTotal = 0;
    wheelTotal += direction * size;
    if (Math.abs(wheelTotal) < WHEEL_THRESHOLD) return;
    wheelTotal = 0;
    gestureLocked = true;
    lockedAt = now;
    step(direction);
  }

  /* ---------- Keyboard ---------- */
  function onKeyDown(event) {
    if (!active || nativeGestures || readingNatively() || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (overlayOpen()) return;
    const focused = document.activeElement;
    if (focused instanceof Element) {
      if (focused.closest(`${IGNORE}, [role="listbox"], [role="radiogroup"], [role="tablist"], [role="menu"], [role="grid"]`)) return;
    }
    let direction = 0;
    switch (event.key) {
      case 'PageDown':
      case 'ArrowDown':
        direction = 1;
        break;
      case 'PageUp':
      case 'ArrowUp':
        direction = -1;
        break;
      case ' ':
      case 'Spacebar':
        // Space activates buttons and summaries; only treat it as paging elsewhere.
        if (focused instanceof Element && focused.closest('button, summary, [role="button"]')) return;
        direction = event.shiftKey ? -1 : 1;
        break;
      case 'Home':
        event.preventDefault();
        if (!animating) animateTo(0);
        return;
      case 'End':
        event.preventDefault();
        if (!animating) animateTo(maxScroll());
        return;
      default:
        return;
    }
    if (canScrollWithin(direction) && !animating) return; // tall panel: native scrolling first
    event.preventDefault();
    if (!animating) step(direction);
  }

  /* ---------- Anchors, focus, history ---------- */
  function panelIndexOf(node, list = panels()) {
    if (!(node instanceof Element)) return -1;
    const y = node.getBoundingClientRect().top + window.scrollY - offset();
    return indexAt(list, Math.max(0, y));
  }

  function focusWithoutScroll(target) {
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function anchorTarget(target) {
    const list = panels();
    const index = panelIndexOf(target, list);
    const item = list[index];
    // Land on the panel when the anchor starts it; otherwise land on the anchor itself.
    const y = target.getBoundingClientRect().top + window.scrollY - offset();
    return item && Math.abs(item.top - y) < viewport() / 3 ? item.top : y;
  }

  function onClick(event) {
    if (nativeGestures && isContact) return;
    if (!active || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search) return;
    const id = decodeURIComponent(url.hash.slice(1));
    if (!id || id === 'MainContent') return;
    const target = document.getElementById(id);
    if (!target || !target.closest('#MainContent, .fw-footer')) return;
    event.preventDefault();
    if (window.location.hash !== url.hash) history.pushState(null, '', url.hash);
    if (nativeGestures && isHome) {
      const clearance = header?.getBoundingClientRect().height || 64;
      scrollInstant(target.getBoundingClientRect().top + window.scrollY - clearance - 12);
      focusWithoutScroll(target);
      return;
    }
    animateTo(anchorTarget(target), () => focusWithoutScroll(target));
  }

  function onFocusIn(event) {
    if (!active || nativeGestures || readingNatively() || animating || overlayOpen()) return;
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('#MainContent, .fw-footer')) return;
    const list = panels();
    const index = panelIndexOf(target, list);
    if (index < 0 || index === currentIndex) return;
    if (!fits(list[index])) return; // tall panel: native focus scrolling keeps the field in view
    goTo(index, 0);
  }

  function onPopState() {
    if (!active || nativeGestures) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id ? document.getElementById(id) : null;
    if (target) animateTo(anchorTarget(target));
    else scheduleSettle();
  }

  /* ---------- Settling after native scroll ---------- */
  function scheduleSettle() {
    window.clearTimeout(settleTimer);
    if (nativeGestures || readingNatively()) { markCurrent(); return; }
    settleTimer = window.setTimeout(settle, 220);
  }

  function onScroll() {
    updateTutorialHeader();
    if (animating) return;
    scheduleSettle();
  }

  let resizeTimer = 0;
  function onResize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      const wasActive = active;
      const index = currentIndex;
      computeActive();
      const list = panels();
      if (nativeGestures || readingNatively() || animating || overlayOpen()) return;
      if (active && wasActive && list[index] && fits(list[index])) scrollInstant(list[index].top);
    }, 150);
  }

  /* ---------- Wire up ---------- */
  previousControl?.addEventListener('click', () => {
    if (animating || overlayOpen()) return;
    if (canScrollWithin(-1)) {
      const list = panels();
      animateTo(Math.max(list[indexAt(list)].top, window.scrollY - viewport()));
    } else step(-1);
  });
  nextControl?.addEventListener('click', () => {
    if (animating || overlayOpen()) return;
    if (canScrollWithin(1)) {
      const list = panels();
      animateTo(Math.min(list[indexAt(list)].bottom - viewport(), window.scrollY + viewport()));
    } else step(1);
  });
  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKeyDown);
  document.addEventListener('click', onClick);
  document.addEventListener('focusin', onFocusIn);
  window.addEventListener('popstate', onPopState);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  window.visualViewport?.addEventListener('resize', onResize);
  reducedMotion.addEventListener('change', computeActive);
  wide.addEventListener('change', markCurrent);
  nativeInput.addEventListener('change', computeActive);

  computeActive();
  // Align a restored or anchor-loaded position onto its panel.
  if (active) scheduleSettle();
})();


